import React, { useEffect, useState } from 'react';
import { format } from 'date-fns';
import { ChevronLeft, ChevronRight, Calendar, Plus } from 'lucide-react';
import { useDashboardStore } from '../stores/dashboardStore';
import CommonActionBar from '../components/CommonActionBar';
import WeeklyBanquetGrid from '../components/WeeklyBanquetGrid';
import BanquetDetailModal from '../components/BanquetDetailModal';
import CreateBanquetBookingModal from '../components/CreateBanquetBookingModal';

const BanquetManagement: React.FC = () => {
  const [showHallModal, setShowHallModal] = useState(false);
  const [showCreateBookingModal, setShowCreateBookingModal] = useState(false);
  const [selectedHallForBooking, setSelectedHallForBooking] = useState<string>('');
  const [selectedDateForBooking, setSelectedDateForBooking] = useState<Date>(new Date());
  
  const {
    banquetHalls,
    calendarDates,
    selectedBanquetHallId,
    isLoading,
    error,
    banquetOrders,
    banquetHallBookings,
    activeBanquetBooking,
    isLoadingBanquetDetails,
    initializeCalendar,
    loadBanquetHalls,
    navigateCalendar,
    selectBanquetBooking,
    clearBanquetSelection,
    getBanquetHallById,
    createBanquetBooking,
  } = useDashboardStore();

  useEffect(() => {
    initializeCalendar();
    loadBanquetHalls();
  }, [initializeCalendar, loadBanquetHalls]);

  const handleBookingSelected = async (bookingId: number) => {
    await selectBanquetBooking(bookingId);
    setShowHallModal(true);
  };

  const handleCloseModal = () => {
    setShowHallModal(false);
    clearBanquetSelection();
  };

  const handleRefresh = () => {
    loadBanquetHalls();
  };

  const handleEmptySpaceClicked = (hallId: string, date: Date) => {
    setSelectedHallForBooking(hallId);
    setSelectedDateForBooking(date);
    setShowCreateBookingModal(true);
  };

  const handleCreateBooking = async (bookingData: any) => {
    try {
      await createBanquetBooking(bookingData);
      setShowCreateBookingModal(false);
      // Optionally show success message
    } catch (error) {
      console.error('Failed to create banquet booking:', error);
      // Error handling is done in the modal
    }
  };

  const handleCloseCreateBookingModal = () => {
    setShowCreateBookingModal(false);
    setSelectedHallForBooking('');
    setSelectedDateForBooking(new Date());
  };

  const handleNewBooking = () => {
    // Open create modal without pre-selecting a hall
    setSelectedHallForBooking('');
    setSelectedDateForBooking(new Date());
    setShowCreateBookingModal(true);
  };

  if (isLoading && banquetHalls.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading banquet halls...</p>
        </div>
      </div>
    );
  }

  if (error && banquetHalls.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-red-600 text-2xl">⚠️</span>
          </div>
          <h3 className="text-lg font-bold text-red-600 mb-2">Error</h3>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={handleRefresh}
            className="btn-primary"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Check if calendarDates has items before accessing
  if (!calendarDates || calendarDates.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading calendar...</p>
        </div>
      </div>
    );
  }

  const startDate = calendarDates[0];
  const endDate = calendarDates[calendarDates.length - 1];
  const weekRange = `${format(startDate, 'MMM dd')} - ${format(endDate, 'MMM dd')}`;

  return (
    <div className="space-y-6">
      {/* Common Action Bar */}
      <CommonActionBar
        showDateTime={false}
        centerAlign={true}
        centerContent={
          <div className="flex items-center space-x-4">
            {/* Previous Week Button */}
            <button
              onClick={() => navigateCalendar(-1)}
              className="flex items-center space-x-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
            >
              <ChevronLeft className="w-4 h-4 text-gray-600" />
              <span className="text-sm font-medium text-gray-700">Previous</span>
            </button>

            {/* Date Range Display */}
            <div className="flex items-center space-x-2">
              <Calendar className="w-4 h-4 text-gray-600" />
              <span className="text-lg font-medium text-gray-800">{weekRange}</span>
            </div>

            {/* Next Week Button */}
            <button
              onClick={() => navigateCalendar(1)}
              className="flex items-center space-x-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
            >
              <span className="text-sm font-medium text-gray-700">Next</span>
              <ChevronRight className="w-4 h-4 text-gray-600" />
            </button>
          </div>
        }
        rightActions={
          <button
            onClick={handleNewBooking}
            className="flex items-center gap-2 bg-primary-500 text-white px-4 py-2 rounded-lg hover:bg-primary-600 transition-colors"
          >
            <Plus className="w-4 h-4" />
            New Booking
          </button>
        }
        onRefresh={handleRefresh}
      />

      {/* Banquet Halls Grid */}
      <WeeklyBanquetGrid
        halls={banquetHalls}
        calendarDates={calendarDates}
        selectedHallId={selectedBanquetHallId}
        onBookingSelected={handleBookingSelected}
        onEmptySpaceClicked={handleEmptySpaceClicked}
      />

      {/* Hall Detail Modal */}
      {showHallModal && (
        <BanquetDetailModal
          hall={getBanquetHallById(selectedBanquetHallId || '')}
          orders={banquetOrders}
          bookings={banquetHallBookings}
          activeBooking={activeBanquetBooking}
          isLoading={isLoadingBanquetDetails}
          onClose={handleCloseModal}
        />
      )}

      {/* Create Booking Modal */}
      {showCreateBookingModal && (
        <CreateBanquetBookingModal
          isOpen={showCreateBookingModal}
          onClose={handleCloseCreateBookingModal}
          hallId={selectedHallForBooking}
          selectedDate={selectedDateForBooking}
          onCreateBooking={handleCreateBooking}
        />
      )}
    </div>
  );
};

export default BanquetManagement;
